import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Card,
    CardContent,
} from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { formatDateAbre, formatDate, formatTimeDecimal } from '../../Components/Funcionais/Utils';

const UserProdutividadeDia = ({ setDiaProdHora, dataProdDia }) => {

    const formatarHourMinute = (valor) => {
        const hours = Math.floor(valor);
        const minutes = (valor - hours) * 60;
        return `${String(hours).padStart(2, '0')}:${String(minutes.toFixed(0)).padStart(2, '0')}`;
    };

    const exibirPrimAtiv = (ativ) => formatTimeDecimal(ativ.primAtividade);
    const exibirUltAtiv = (ativ) => formatTimeDecimal(ativ.ultAtividade);

    const todosHorarios = [
        ...dataProdDia.map((item) => exibirPrimAtiv(item)),
        ...dataProdDia.map((item) => exibirUltAtiv(item))
    ];

    const minHorario = Math.floor(Math.min(...todosHorarios));
    const maxHorario = Math.ceil(Math.max(...todosHorarios));

    const chartOptionsMult = {
        tooltip: {
            enabled: true,
            theme: "light",
            y: {
                formatter: function (value, { seriesIndex, dataPointIndex }) {
                    const item = dataProdDia[dataPointIndex];

                    if (seriesIndex === 0 && item) {
                        const perc = value.toFixed(0);
                        return `${perc}%  =  Minutos Produzidos (${item.minRegistrados}) / Minutos Disponíveis (${item.minTotal})`;
                    }

                    return value;
                }
            }
        },
        chart: {
            type: "line",
            height: 350,
            toolbar: { show: false },
            background: "transparent",
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    const dataPointIndex = config.dataPointIndex;
                    setDiaProdHora(formatDate(dataProdDia[dataPointIndex].data));
                }
            },
        },
        colors: ["#647DEE", "#CA6111", "#f44336"],
        fill: {
            type: ["gradient", "solid", "solid"],
            gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.3,
                gradientToColors: ["#1A3865"],
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 0.85,
            },
        },
        stroke: {
            width: [0, 3, 3],
            curve: "smooth",
        },
        markers: {
            size: [0, 4, 4],
        },
        plotOptions: {
            bar: {
                borderRadius: 8,
                dataLabels: { position: "top" },
                columnWidth: "65%",
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val, { seriesIndex }) => {
                if (seriesIndex === 0) return `${val.toFixed(0)}%`;
                return formatarHourMinute(val);
            },
            style: {
                fontSize: "10px",
                fontWeight: 700,
                colors: ["#1A3865", "#7A4010", "#b71c1c"],
            },
            offsetY: -4,
        },
        grid: {
            borderColor: "#e0e7ff",
            strokeDashArray: 4,
            padding: { top: 0, right: 10, bottom: 0, left: 10 },
        },
        xaxis: {
            categories: dataProdDia.map((item) => `${formatDateAbre(item.data)}`),
            position: "bottom",
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: {
                style: {
                    fontSize: "10px",
                    colors: "#6b7a99",
                    fontWeight: 500,
                },
            },
        },
        yaxis: [
            {
                max: 100,
                min: 0,
                labels: {
                    formatter: (val) => `${val.toFixed(0)}%`,
                    style: { colors: "#6b7a99", fontSize: "11px" },
                },
                title: {
                    text: "Aproveitamento",
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#1A3865",
                        fontFamily: "inherit",
                    },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            {
                labels: {
                    formatter: (val) => formatarHourMinute(val),
                    style: { colors: "#6b7a99", fontSize: "11px" },
                },
                opposite: true,
                title: {
                    text: "Início e Fim de Atividades",
                    style: {
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#CA6111",
                        fontFamily: "inherit",
                    },
                },
                min: minHorario,
                max: maxHorario,
            },
        ],
        title: { text: "" },
        legend: {
            labels: {
                colors: ["#1A3865", "#CA6111", "#f44336"],
            },
        },
    };

    return (
        <Box sx={{ width: "100%", mt: 2, position: "relative" }}>
            <Card sx={{
                width: "100%",
                boxShadow: "0 8px 28px rgba(26, 56, 101, 0.12), 0 2px 6px rgba(100,125,238,0.08)",
                borderRadius: "16px",
                border: "1px solid rgba(100, 125, 238, 0.15)",
                overflow: "hidden",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0, left: 0, right: 0,
                    height: "4px",
                    background: "linear-gradient(90deg, #269FFB, #647DEE, #1A3865)",
                },
            }}>
                <CardContent sx={{ pt: 3 }}>
                    <Typography gutterBottom align="center" sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#1A3865",
                        letterSpacing: "0.4px",
                    }}>
                        Aproveitamento por Dia
                    </Typography>
                    <ReactApexChart
                        options={chartOptionsMult}
                        series={[
                            {
                                name: "Aproveitamento",
                                type: "column",
                                data: dataProdDia.map((item) => item.minRegistrados * 100 / item.minTotal > 100 ? 100 : (item.minRegistrados * 100 / item.minTotal).toFixed(0)),
                            },
                            {
                                name: "Início de Atividades",
                                type: "line",
                                data: dataProdDia.map((item) => exibirPrimAtiv(item)),
                            },
                            {
                                name: "Fim de Atividades",
                                type: "line",
                                data: dataProdDia.map((item) => exibirUltAtiv(item)),
                            },
                        ]}
                        type="bar"
                        height={350}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

UserProdutividadeDia.propTypes = {
  setDiaProdHora: PropTypes.func,
  dataProdDia: PropTypes.array,
};

export default UserProdutividadeDia;
